import { useState, useCallback } from "react";
import displayNames from "../constants/displayNames.json";

const useValidation = (fields) => {
  const [errors, setErrors] = useState({});

  const getFieldDisplayName = useCallback((field) => {
    return (
      displayNames[field] || field.charAt(0).toUpperCase() + field.slice(1)
    );
  }, []);

  const validateField = useCallback(
    (field, value) => {
      switch (field) {
        case "firstName":
        case "lastName":
          if (!value)
            return `Please enter your ${getFieldDisplayName(field).toLowerCase()}`;
          if (!/^[A-Za-zÀ-ÿ]+$/.test(value)) {
            return `${getFieldDisplayName(field)} must contain only letters`;
          }
          break;
        case "email":
          if (!value) return `Please enter your email address`;
          if (
            !/^(?!\d+@)\w+([-+.']\w+)*@(?!\d+\.)\w+([-.]\w+)*\.\w+([-.]\w+)*$/.test(
              value,
            )
          ) {
            return `Please enter a valid email address`;
          }
          break;
        case "password":
          if (!value) return `Please enter your password`;
          if (
            !/^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(value)
          ) {
            return `${getFieldDisplayName(
              field,
            )} must contain at least 8 characters, including uppercase, lowercase letters, special characters and a number`;
          }
          break;
        // Existence-only checks: complexity rules apply when SETTING a
        // password, not when verifying one — the server validates
        // correctness, and passwords predating the current rules (or set
        // out-of-band) must still be usable to log in / be changed.
        case "loginPassword":
        case "currentPassword":
          if (!value) return `Please enter your password`;
          break;
        case "newPassword":
          if (!value) return `Please enter your new password password`;
          if (
            !/^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(value)
          ) {
            return `${getFieldDisplayName(
              field,
            )} must contain at least 8 characters, including uppercase, lowercase letters, special characters and a number`;
          }
          if (fields.currentPassword === fields.newPassword)
            return `Current Password and New Password cannot be the same`;
          break;
        case "confirmPassword":
          if (!value) return `Please enter your confirm password`;
          if (
            !/^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(value)
          ) {
            return `${getFieldDisplayName(
              field,
            )} must contain at least 8 characters, including uppercase, lowercase letters, special characters and a number`;
          }
          {
            const testField = fields.password || fields.newPassword;
            if (testField !== value) return `Passwords do not match`;
          }
          break;
        case "termsCondition":
          if (!value)
            return `Please accept our Terms of Service & Privacy Policy`;
          break;
        case "phone":
          if (!value) return `Please enter your Phone number`;
          if (
            !/^(?!000000000|000-000-0000)\d{3}[-\s]?\d{3}[-\s]?\d{4}$/.test(
              value,
            )
          ) {
            return `Please enter a valid phone number`;
          }
          break;
        case "jobTitle":
        case "employer":
        case "city":
        case "province":
          if (!value)
            return `Please enter your ${getFieldDisplayName(field).toLowerCase()}`;
          if (!/^[A-Za-z&'.,-]+(?: [A-Za-z&'.,-]+)*$/.test(value?.trim())) {
            return `${getFieldDisplayName(field)} must contain only letters`;
          }
          break;
        case "sponsorCode":
          if (!value) return `Please enter your ${getFieldDisplayName(field)}`;
          if (
            !/^(?!^[^A-Za-z]*$)(?!^\d+$)(?!^[\W_]+$).+$/.test(field?.license)
          ) {
            return `${getFieldDisplayName(field)} must contain only alphanumeric characters`;
          }
          break;
        default:
          return "";
      }
      return "";
    },
    [
      getFieldDisplayName,
      fields.password,
      fields.newPassword,
      fields.currentPassword,
    ],
  );

  const validateInputs = useCallback(() => {
    const newErrors = {};

    Object.keys(fields).forEach((field) => {
      const value = fields[field];
      const errorMessage = validateField(field, value);

      if (errorMessage) {
        newErrors[field] = errorMessage;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [fields, validateField]);
  const resetValidation = useCallback(() => {
    setErrors({});
  }, []);
  return { errors, validateInputs, resetValidation };
};

export default useValidation;
